<template>
	<div
		class="textarea-wrapper"
		:class="{
			'change-disabled': isDisabled,
			'validation-error': validationError,
			'readonly-mode': readonly
		}"
	>
		<label v-if="label">
			{{ label }}
		</label>
		<textarea
			v-model="currentValue"
			:name="inputField"
			:style="inputStyle"
			:placeholder="placeholder"
			:disabled="isDisabled"
			:maxlength="symbolsLimit"
			:autocomplete="autocomplete"
			:autocorrect="autocorrect"
			:spellcheck="spellcheck"
			:readonly="readonly"
			@input="onChange"
			@change="onChange"
			@blur="onBlur"
		/>
		<textarea
			ref="shadow"
			v-model="currentValue"
			class="shadow"
			tabindex="-1"
			:disabled="isDisabled"
			:placeholder="placeholder"
			:maxlength="symbolsLimit"
		/>
		<div
			v-if="!readonly && isCounterVisible && leftTenSymbols"
			class="limit-info"
		>
			<span :class="{ 'alert-color': isHighlighted }">
				{{ currentValue.length }} </span
			>/{{ symbolsLimit }}
		</div>
		<div v-if="validationError" class="validation-message">
			{{ validationError }}
		</div>
	</div>
</template>

<script>
export default {
	name: 'AutoResizeTextarea',

	props: {
		value: {
			type: [String, null],
			default: ''
		},
		placeholder: {
			type: String,
			default: ''
		},
		label: {
			type: String,
			default: ''
		},
		symbolsLimit: {
			type: [String, Number],
			default: '500'
		},
		maxHeight: {
			type: Number,
			default: 1000
		},
		isDisabled: {
			type: Boolean,
			default: false
		},
		readonly: {
			type: Boolean,
			default: false
		},
		errors: {
			type: Object,
			default: () => ({})
		},
		inputField: {
			type: String,
			default: 'AutoResizeTextarea'
		},
		autocomplete: {
			type: String,
			default: 'off'
		},
		autocorrect: {
			type: String,
			default: 'off'
		},
		spellcheck: {
			type: String,
			default: 'false'
		},
		isCounterVisible: {
			type: Boolean,
			default: true
		},
		isRerender: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			currentValue: '',
			inputHeight: '0',
			verticalPadding: 13,
			showCounterLimit: 10
		}
	},

	computed: {
		inputStyle() {
			const minHeight =
				this.maxHeight > this.inputHeight ? this.inputHeight : this.maxHeight

			return {
				'padding-top': `${this.verticalPadding}px`,
				'padding-bottom': `${this.verticalPadding}px`,
				'min-height': `${minHeight + 2}px` // +1px for border height +1px for wrong shadow textarea height calculation on Windows
			}
		},

		currentLength() {
			return this.currentValue.length
		},

		isHighlighted() {
			return this.currentLength >= this.symbolsLimit
		},

		validationError() {
			const errorField = this.errors && this.errors[this.inputField]

			return errorField && Array.isArray(errorField) ? errorField[0] : false
		},

		leftTenSymbols() {
			return this.symbolsLimit - this.currentLength <= this.showCounterLimit
		}
	},

	watch: {
		value(nv) {
			this.setValue(nv)
			this.$nextTick(this.resize)
		},
		isRerender() {
			this.$nextTick(this.resize)
		}
	},

	mounted() {
		this.setValue(this.value)
		this.$nextTick(() => {
			// timeout for correct height (font rendering issue)
			setTimeout(this.resize, 0)
		})
	},

	methods: {
		resize() {
			if (this.$refs.shadow) {
				this.inputHeight =
					this.$refs.shadow.scrollHeight + this.verticalPadding * 2
			}
		},

		onChange(e) {
			this.$nextTick(() => {
				this.resize()
				if (this.isDisabled || !e) {
					return
				}
				this.$emit('on-change', e.target.value)
			})
		},

		onBlur(e) {
			if (this.isDisabled || !e) {
				return
			}
			this.$emit('on-blur', e.target.value)
		},

		setValue(value) {
			this.currentValue = typeof value === 'string' ? value : ''
		}
	}
}
</script>

<style lang="scss" scoped>
@import './src/assets/styles/variables';

.textarea-wrapper {
	position: relative;
	overflow: hidden;
	width: 100%;
	padding: 0 0 16px;

	&.validation-error textarea {
		border-color: $alertRed;
	}

	&.change-disabled {
		opacity: 0.5;
		pointer-events: none;

		textarea {
			opacity: 0.5;
			pointer-events: none;
		}
	}

	textarea {
		overflow: auto;
		width: 100%;
		height: 0;
		box-sizing: border-box;
		padding: 14px 12px;
		background-color: $background-box;
		border: 1px solid $border-color;
		border-radius: 8px;
		resize: none;
		transition: border-color 0.3s, background-color 0.3s;

		&:focus {
			border-color: lighten($border-color, 30%);
		}

		&.shadow {
			position: absolute;
			opacity: 0;
			height: 0;
			max-height: 0;
			margin: 0;
			padding-top: 0;
			padding-bottom: 0;
			pointer-events: none;
		}
	}

	label {
		font-size: 10px;
		text-transform: uppercase;
		font-weight: 400;
	}
}

.limit-info {
	position: absolute;
	bottom: -1px;
	right: 0;
	font-size: 10px;
	color: $white;

	.alert-color {
		color: $alertRed;
	}
}

.validation-message {
	position: absolute;
	right: 50px;
	bottom: 0;
	left: 0;
	display: block;
	white-space: nowrap;
	overflow: hidden;
	font-size: 12px;
	color: $alertRed;
}
</style>
